import { AssetGroup } from '@play-co/timestep-core';
import View from '@play-co/timestep-core/lib/ui/View';
import { ImageScaleView, ImageView } from '@play-co/timestep-core/ui';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import { Feature } from 'src/replicant/analytics';
import sounds from 'src/lib/sounds';
import { DOWNSCALE_FACTOR } from 'src/conf/scale';

type CloseHandler = () => void;

// QR code size for fullscreen display
const QR_CODE_SIZE = 520;

/**
 * Fullscreen popup for displaying the QR code in Add Friends V2.
 * Provides clear exit guidance with a close button and tap-to-dismiss.
 */
export default class PopupAddFriendQRFullscreen extends PopupBasic<CloseHandler> {
  private qrCodeImage: ImageView;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => {
        opts.close();
      },
      zIndex: 10002,
      width: 640,
      height: 680,
      skipTitle: true,
      skipMessage: true,
      closeableWithBg: true,
      darkerBg: true,
      tracking: {
        feature: Feature.INVITE._,
        $subFeature: Feature.INVITE.NATIVE_SHARE,
      },
    });

    const boxWidth = this.box.style.width;
    const boxHeight = this.box.style.height;
    this.buttonClose.tracking = {
      feature: Feature.INVITE._,
      $subFeature: Feature.INVITE.NATIVE_SHARE,
    };

    // QR Code with rounded corners frame (tappable to close)
    const qrCodeFrame = new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/friends/popupAddFriend/qrcode_frame.png',
      x: boxWidth / 2,
      y: boxHeight / 2,
      width: QR_CODE_SIZE + 32,
      height: QR_CODE_SIZE + 32,
      centerOnOrigin: true,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 40, right: 40 },
        vertical: { top: 40, bottom: 50 },
      },
      imgScale: DOWNSCALE_FACTOR,
    });

    // Make the frame tappable to close the popup
    qrCodeFrame.onInputSelect = () => {
      sounds.sfx.play('cancel');
      opts.close();
    };

    // QR code image inside the frame
    this.qrCodeImage = new ImageView({
      superview: qrCodeFrame,
      width: QR_CODE_SIZE,
      height: QR_CODE_SIZE,
      centerOnOrigin: true,
      x: qrCodeFrame.style.width / 2,
      y: qrCodeFrame.style.height / 2 - 10,
    });
  }

  protected getAssetsGroup(): AssetGroup | null {
    // Load QR code frame asset
    return new AssetGroup(
      AssetGroup.constructURLs(['assets/ui/friends/popupAddFriend']),
    );
  }

  /**
   * Initialize the popup with the QR code data URL
   */
  public init(args: { qrCodeDataUrl: string }) {
    super.init({});
    this.qrCodeImage.setImage(args.qrCodeDataUrl);
  }
}
